<template>
  <div class="email-token-login-form">
    <!-- Password input phase (Virtual User) -->
    <template v-if="showPasswordInput">
      <CInput
        ref="password"
        :placeholder="$pgettext('auth.placeholder','Password')"
        v-model="password"
        :label="$gettext('Password')"
        type="password"
        autocomplete="current-password"
        @keyup.enter="loginWithPassword"
        :is-valid="!passwordError">
        <template v-slot:invalid-feedback>
          <div class="invalid-feedback d-block" v-if="passwordError">
            <div class="error-msg">{{ passwordError }}</div>
          </div>
        </template>
      </CInput>

      <PButton :loading="loading" type="button" color="primary" block @click="loginWithPassword">
        <translate translate-context="login.button">Login</translate>
      </PButton>

      <div class="text-center mt-3">
        <CLink @click="resetForm" class="text-muted small">
          <translate translate-context="login.virtualuser">Use a different email</translate>
        </CLink>
      </div>
    </template>

    <!-- Token input phase -->
    <template v-else-if="showTokenInput">
      <div class="text-center mb-4" style="font-size: 16px">
        <translate translate-context="login.token">If your email is registered, you will receive a verification code</translate><br>
        <span class="font-weight-semi-bold">{{ email }}</span>
      </div>

      <TokenInput
        :validating="validatingToken"
        :error-message="tokenError"
        :show-resend="true"
        @complete="onTokenComplete"
        @resend="resendToken"
        ref="tokenInput"
      />

      <!-- Alternative login methods -->
      <div class="text-center mt-3">
        <CLink v-for="provider in alternativeProviders" :key="provider"
               @click="$emit('switch-provider', provider)"
               class="text-muted small d-block mb-1">
          <translate translate-context="login.alternative">{{ getProviderLabel(provider) }}</translate>
        </CLink>
      </div>
    </template>

    <!-- Email input phase -->
    <template v-else>
      <CInput
        ref="email"
        :placeholder="$pgettext('auth.placeholder','E-mail')"
        v-model="v$.email.$model"
        :label="$gettext('E-mail')"
        :is-valid="!v$.email.$error && !emailError"
        autocomplete="username email"
        @keyup.enter="requestToken">
        <template v-slot:invalid-feedback>
          <div class="invalid-feedback d-block" v-for="error of v$.email.$errors" :key="error.$uid">
            <div class="error-msg">{{ error.$message }}</div>
          </div>
          <div class="invalid-feedback d-block" v-if="emailError">
            <div class="error-msg">{{ emailError }}</div>
          </div>
        </template>
      </CInput>

      <PButton :loading="loading" type="button" color="primary" block @click="requestToken">
        <translate translate-context="login.button">Continue</translate>
      </PButton>

      <!-- Alternative login methods -->
      <div class="text-center mt-3">
        <CLink v-for="provider in alternativeProviders" :key="provider"
               @click="$emit('switch-provider', provider)"
               class="text-muted small d-block mb-1">
          <translate translate-context="login.alternative">{{ getProviderLabel(provider) }}</translate>
        </CLink>
      </div>
    </template>
  </div>
</template>

<script>
import PButton from "@/components/PButton.vue";
import TokenInput from "@/domain/login/components/TokenInput.vue";
import {useVuelidate} from "@vuelidate/core";
import {email$, required$} from "@/domain/core/utils/validators";
import {AuthProviders} from "@/domain/login/constant/authProviders";
import {mapActions, mapGetters} from "vuex";

export default {
  name: 'EmailTokenLoginForm',
  components: {PButton, TokenInput},
  props: {
    initialEmail: {
      type: String,
      default: null
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  data() {
    return {
      email: this.initialEmail,
      password: '',
      showTokenInput: false,
      showPasswordInput: false,
      validatingToken: false,
      tokenError: null,
      passwordError: null,
      emailError: null,
      submitted: false,
      loading: false
    }
  },
  validations() {
    return {
      email: {required$, email$}
    }
  },
  computed: {
    ...mapGetters('login', ['authProviderFactory', 'registrationFlow']),
    alternativeProviders() {
      const provider = this.authProviderFactory.getProvider(AuthProviders.EMAIL_TOKEN)
      return provider.getAlternatives((p) => this.authProviderFactory.isEnabled(p))
    }
  },
  mounted() {
    if (this.initialEmail) {
      this.showTokenInput = true
    } else if (this.$refs.email) {
      this.$refs.email.$el.children[0].focus()
    }
  },
  watch: {
    initialEmail(val) {
      this.email = val
      if (val) {
        this.showTokenInput = true
      }
    },
    email() {
      // Pulisci errore email quando l'utente modifica il campo
      this.emailError = null
    }
  },
  methods: {
    ...mapActions('login', ['authenticate', 'sendLoginToken', 'validateCode', 'resendValidationCode']),

    async requestToken() {
      if (!await this.v$.$validate()) return

      this.submitted = true
      if (this.v$.$invalid) return

      try {
        this.loading = true
        await this.sendLoginToken({email: this.email})
        this.showTokenInput = true
        this.clearTokenError()
      } catch (e) {
        // Check if this is a virtual user
        if (e.data?.code === 'virtual_user_no_token') {
          this.showPasswordInput = true
          this.showTokenInput = false
          // Focus password field on next tick
          this.$nextTick(() => {
            if (this.$refs.password) {
              this.$refs.password.$el.children[0].focus()
            }
          })
        } else if (e.data?.code === 'user_not_found_for_login_token') {
          // Utente non trovato o inattivo - rimani sulla schermata email e mostra errore
          this.emailError = this.$pgettext('login.error', 'User not found or inactive')
        } else {
          // Per sicurezza, mostra sempre lo stesso messaggio generico
          // Non rivelare se l'email esiste o meno nel sistema
          this.showTokenInput = true
          this.clearTokenError()
        }
      } finally {
        this.loading = false
      }
    },

    async onTokenComplete(token) {
      this.validatingToken = true

      try {
        // Use different endpoint based on registration flow
        if (this.registrationFlow === 'validation_required') {
          // New user registration - validate registration token
          await this.validateCode({email: this.email, code: token})
        } else {
          // Existing user - authenticate with login token
          await this.authenticate({
            method: AuthProviders.EMAIL_TOKEN,
            credentials: {email: this.email, token},
            remember: true
          })
        }

        this.$emit('login-success')
      } catch (e) {
        // Gestione errore specifico per token invalido o scaduto
        let errorMsg = this.$pgettext('login.error', 'Invalid code')

        if (e.data?.code === 'invalid_login_token' || e.data?.code === 'invalid_registration_token') {
          errorMsg = this.$pgettext('login.error', 'Invalid or expired code')
        }

        this.setTokenError(errorMsg)
      } finally {
        this.validatingToken = false
      }
    },

    async resendToken() {
      try {
        // Use different resend method based on registration flow
        if (this.registrationFlow === 'validation_required') {
          await this.resendValidationCode({email: this.email})
        } else {
          await this.sendLoginToken({email: this.email})
        }

        this.clearTokenError()
      } catch (e) {
        // Per sicurezza, non mostrare errori specifici
        // Comportamento coerente: non rivelare se l'email esiste o meno
        this.clearTokenError()
      }
    },

    setTokenError(error) {
      this.tokenError = error
    },

    clearTokenError() {
      this.tokenError = null
    },

    async loginWithPassword() {
      if (!this.password) {
        this.passwordError = this.$pgettext('login.error', 'Password is required')
        return
      }

      this.passwordError = null
      this.loading = true

      try {
        await this.authenticate({
          method: AuthProviders.PASSWORD,
          credentials: {email: this.email, password: this.password},
          remember: true
        })

        this.$emit('login-success')
      } catch (e) {
        if (e.data?.code === 'invalid_credentials') {
          this.passwordError = this.$pgettext('login.error', 'Invalid email or password')
        } else {
          this.passwordError = this.$pgettext('login.error', 'Login failed. Please try again.')
        }
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.email = ''
      this.password = ''
      this.showTokenInput = false
      this.showPasswordInput = false
      this.tokenError = null
      this.passwordError = null
      this.emailError = null
      this.submitted = false

      this.$nextTick(() => {
        if (this.$refs.email) {
          this.$refs.email.$el.children[0].focus()
        }
      })
    },

    getProviderLabel(provider) {
      switch (provider) {
        case AuthProviders.LDAP:
          return this.$pgettext('login.alternative', 'Use company credentials')
        case AuthProviders.PASSWORD:
          return this.$pgettext('login.alternative', 'Use email and password')
        case AuthProviders.GOOGLE:
          return this.$pgettext('login.alternative', 'Use Google')
        case AuthProviders.FACEBOOK:
          return this.$pgettext('login.alternative', 'Use Facebook')
        case AuthProviders.APPLE:
          return this.$pgettext('login.alternative', 'Use Apple')
        default:
          return provider
      }
    }
  }
}
</script>

<style scoped>
.email-token-login-form {
  width: 100%;
}
</style>
